import { pressureToWidth } from './smoothing';

/**
 * Build a filled polygon (flat [x0,y0,x1,y1,...]) approximating a
 * variable-width stroke ribbon. Left offsets forward, right offsets reversed,
 * so the polygon closes cleanly. Single point → circle fan (dot).
 * Miter length is clamped to 2× half-width to avoid spikes at sharp turns.
 */
export function buildRibbon(points: Float32Array, baseWidth: number): number[] {
  const n = points.length / 4;
  if (n === 0) return [];
  if (n === 1) {
    const x = points[0]!, y = points[1]!;
    const r = pressureToWidth(points[2]!, baseWidth) / 2;
    const out: number[] = [];
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      out.push(x + r * Math.cos(a), y + r * Math.sin(a));
    }
    return out;
  }
  const left: number[] = [], right: number[] = [];
  for (let i = 0; i < n; i++) {
    const x = points[i * 4]!, y = points[i * 4 + 1]!;
    const half = pressureToWidth(points[i * 4 + 2]!, baseWidth) / 2;
    // direction: average of incoming and outgoing segment directions
    let dx = 0, dy = 0;
    if (i > 0) { dx += x - points[(i - 1) * 4]!; dy += y - points[(i - 1) * 4 + 1]!; }
    if (i < n - 1) { dx += points[(i + 1) * 4]! - x; dy += points[(i + 1) * 4 + 1]! - y; }
    const len = Math.hypot(dx, dy) || 1;
    // normal (perpendicular), miter-clamped
    let nx = -dy / len, ny = dx / len;
    left.push(x + nx * half, y + ny * half);
    right.push(x - nx * half, y - ny * half);
  }
  // close: left forward, then right reversed
  const out = left.slice();
  for (let i = right.length - 2; i >= 0; i -= 2) out.push(right[i]!, right[i + 1]!);
  return out;
}

/** Fill a ribbon polygon onto a 2D context (ctx state — fillStyle — set by caller). */
export function fillRibbon(ctx: CanvasRenderingContext2D, poly: number[]): void {
  if (poly.length < 6) return;
  ctx.beginPath();
  ctx.moveTo(poly[0]!, poly[1]!);
  for (let i = 2; i < poly.length; i += 2) ctx.lineTo(poly[i]!, poly[i + 1]!);
  ctx.closePath();
  ctx.fill();
}
